import {BehaviorSubject, Observable, ReplaySubject, Subject} from "rxjs";
import {ItemEvent} from "../classes/ItemEvent";
import {ItemConfig} from "./ItemConfig";
import {appInjector} from "../classes/appInjector";
import {ServerConnectionService} from "../services/serverConnection/server-connection.service";
import {ConcreteHierarchyItemInterface} from "../classes/concreteLayers/ConcreteHierarchyItemInterface";
import {isRemoveSidebar} from "../../configs/view_only";
import {PaywallWarnService} from '@modules/paywall/services/paywallWarnService/paywall-warn.service';

export class Item implements ConcreteHierarchyItemInterface {

  /** @deprecated */
  public group = null;
  public isDrawn = false;
  protected objectRemoved = false;

  protected children: Map<string, ConcreteHierarchyItemInterface> = new Map<string, ConcreteHierarchyItemInterface>();
  protected childrenObservable: Observable<ConcreteHierarchyItemInterface[]>;
  protected childrenObservable$: BehaviorSubject<ConcreteHierarchyItemInterface[]> = new BehaviorSubject<ConcreteHierarchyItemInterface[]>([]);

  public _drag_enabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  get drag_enabled(){
    return this._drag_enabled.getValue();
  }
  set drag_enabled(val){
    this._drag_enabled.next(val);
  }

  get dragChanged(): Observable<boolean> {
    return this._drag_enabled.asObservable();
  }

  public getParentGroupId(){
    return this.config.getGroupId();
  }

  // drag timer
  public timer: any = null;

  public config: ItemConfig = new ItemConfig();

  public registeredPlugins = [];

  private subjects: { [key: string]: Subject<any> } = {};
  private counterSubjects: { [key: string]: BehaviorSubject<number> } = {};
  private subjectValues: { [key: string]: any } = {};
  private paywallWarnService: PaywallWarnService;
  public static MEASUREMENT_INPUT_NAME = '__measurement_system';
  public static SUBJECT_ROTATE_BY = 'rotateBy';
  public static SUBJECT_ROTATE_TO = 'rotateTo';
  public static OBJECT_CLICK = 'objectClick';
  public static LABEL_VISIBILITY = 'labelVisibility';
  public static SUBJECT_INPUTS_CHANGED = 'inputsChanged';
  public static readonly EXECUTE_STATIC_INPUTS_METHOD = 'executeStaticInputsMethod';
  public static SUBJECT_VISIBILITY = 'objectVisibility';
  public static MODEL_TRANSPARENCY = 'modelTransparency';
  public static SUBJECT_LEFT_SIDEBAR_MAIN_VISIBILITY = 'itemSidebarMainVisibility';
  public static SUBJECT_SIDEBAR_VISIBILITY = 'itemSidebarVisibility';
  public static OBJECT_OFFSET_BY = 'objectOffsetBy';
  public static MOVE_OBJECT_BY_OFFSET = 'objectOffsetByMultiSelect';
  public static OBJECT_OFFSET_WITH_DIRECTION = 'objectOffsetWithDirection';
  public static URL_UPLOAD_PHOTO = window['URL_UPLOAD_PHOTO'];
  public static URL_UPLOAD_FILE = window['URL_UPLOAD_FILE'];
  public static UNDO_SUBJECT = 'undoAction';
  public static AFTER_DRAWN_SUBJECT = 'afterDrawn';
  public static BABY_SUBJECT = 'babyChanged';
  public static readonly ParticipantName = '__participant_groups';
  public static DRAWN_ITEM_ADDED = 'drawnItemAdded';
  public static SET_UP_AREA_INPUT = '__setUpArea';
  public static SET_UP_ITEM_INPUT = '__setUpItem';
  public static APPLY_NEW_TOOL = 'applyNewTool';
  public static SET_NEW_PARENT_FOR_OBJECT_ARRAY_COPY = 'SET_NEW_PARENT_FOR_OBJECT_ARRAY_COPY';
  // public mapObject;
  static plugins = [];

  public emitter: Subject<ItemEvent> = new Subject<ItemEvent>();

  protected visibilityComponents = {};

  static addPlugin(plugin) {
    Item.plugins.push(plugin);
  }

  constructor(config, isWithoutSave?) {
    this.config.assign(config, isWithoutSave);
    if (!this.config.isValid()) {
      throw new Error('Invalid object config');
    }
    // this.id = this.config.getId();
    this.group = this.config.getGroup();

    for (let i = 0; i < Item.plugins.length; i++) {
      Item.plugins[i].registerOnItem(this);
    }

    this.getSubject(Item.SUBJECT_VISIBILITY)
        .subscribe((val) => {
          if (!val) {
            if (this.getObservable('highlight_object')) {
              this.getObservable('highlight_object').next(false);
            }
          }
        });

    this.paywallWarnService = appInjector.get(PaywallWarnService);
  }

  /**
   * Legacy structure required by calculations
   * @deprecated
   */
  get objects() {
    return [this.config.getObjectConfig()];
  }

  /**
   * Legacy structure required by calculations
   * @deprecated
   */
  get inputs() {
    return this.config.getInputs();
  }

  /**
   *
   */
  getConvertedInputValue(inputName) {
    if (this.config._convertedInputs.hasOwnProperty(inputName)) {
      return this.config._convertedInputs[inputName];
    }
    return this.config.getInputProp(inputName, 'value');
  }

  public getId(){
    return this.config.getId();
  }

  public getGroup(){
    return this.config.getGroup();
  }

  public toggleLabelVisibility(flag){
    this.getSubject(Item.LABEL_VISIBILITY).next(flag);
  }

  /**
   * return subject with transmitted token
   *
   * @param subjectName - subject token
   */
  public getObservable(subjectName: string): Subject<any> {
    if (!this.subjects.hasOwnProperty(subjectName)) {
      this.subjects[subjectName] = new Subject();
      this.subjectValues[subjectName] = undefined;
      if (!isRemoveSidebar) {
        this.subjects[subjectName].subscribe(data => this.subjectValues[subjectName] = data);
      }
    }
    return this.subjects[subjectName];
  }

  public getSubject(subjectName : string) : Subject<any> {
      if (!this.subjects.hasOwnProperty(subjectName)) {
        this.subjects[subjectName] = new ReplaySubject(1);
        this.subjectValues[subjectName] = undefined;
        if (!isRemoveSidebar) {
          this.subjects[subjectName].subscribe(data => this.subjectValues[subjectName] = data);
        }
      }
      return this.subjects[subjectName];
  }

  public getCounter(counterName : string) : BehaviorSubject<number> {
    if (!this.counterSubjects.hasOwnProperty(counterName)) {
      this.counterSubjects[counterName] = new BehaviorSubject<number>(0);
    }
    return this.counterSubjects[counterName];
  }

  public getCounterValue(counterName : string):number{
    if (!this.counterSubjects.hasOwnProperty(counterName)) {
      this.counterSubjects[counterName] = new BehaviorSubject<number>(0);
      return 0;
    }
    return this.counterSubjects[counterName].getValue();
  }

  public incrementCounter(counterName){
    if (!this.counterSubjects.hasOwnProperty(counterName)) {
      this.counterSubjects[counterName] = new BehaviorSubject<number>(0);
    }
    let val = this.counterSubjects[counterName].getValue();
    val++;
    this.counterSubjects[counterName].next(val);
  }

  clearCounter(counterName){
    if (!this.counterSubjects.hasOwnProperty(counterName)) {
      this.counterSubjects[counterName] = new BehaviorSubject<number>(0);
    }
    this.counterSubjects[counterName].next(0);
  }

  public decrementCounter(counterName){
    if (!this.counterSubjects.hasOwnProperty(counterName)) {
      this.counterSubjects[counterName] = new BehaviorSubject<number>(0);
    }
    let val = this.counterSubjects[counterName].getValue();
    val--;
    this.counterSubjects[counterName].next(val);
  }

  public getSubjectValue(subjectName: string): any {
    return (this.subjectValues.hasOwnProperty(subjectName) ? this.subjectValues[subjectName] : undefined);
  }

  public onAddedToItemManager() {
    let e = new ItemEvent(this, 'addedToItemManager');
    this.emitter.next(e);
  }

  public save(){
    return new Promise((resolve, reject)=>{
      if (this.config.getId()){
        resolve('updated');
      }
      else {
        if (!isRemoveSidebar) {
          appInjector.get(ServerConnectionService).addObject(this.config.asSimpleObject()).subscribe((result) => {
            if (result){
              let data = result.data || result;

              if (result.error && !result.success){
                this.paywallWarnService.processResponse(result);

                reject(data);
                return;
              }

              this.addMissingInputs(data);


              if (data.hasOwnProperty('id')) {
                this.config.setId(parseInt(data.id));
                resolve('added');
                this.paywallWarnService.processResponse(result);
              }  else {
                reject('Add object has no id');
              }

            }

            if (!result){
              reject('Add object has null result');
            }

          }, (err)=>{
            console.error('AddObjectError', err);
            reject('AddObjectError '+JSON.stringify(err));
          });
        }
      }
    })
  }

  public prepareItemConfigForServer(){

    return this.config;
  }

  /**
   *
   * @param config - item object from server
   */
  public onItemUpdated(config){
    this.config.assign(config);
  }

  public clickOnObject(object:{entityId, isCtrl}){
    this.getSubject(Item.OBJECT_CLICK).next(object.isCtrl);
  }

  public setTitle(val){
    this.config.setTitle(val);
  }

  public objectChanged(){
    let e = new ItemEvent(this, 'objectChanged');
    // e.methodName = 'updateObject';
    e.preparedItemConfig = this.prepareItemConfigForServer();
    this.emitter.next(e);
  }

  public onRotationChange(degrees){

  }

  public centerOnObj(){
    let e = new ItemEvent(this, 'centerOnObject');
    this.emitter.next(e);
  }

  public setVisibility(value) {
    this.getSubject(Item.SUBJECT_VISIBILITY).next(value);
  }

  public isVisible() {
    return !!this.getSubjectValue(Item.SUBJECT_VISIBILITY);
  }

  public setSidebarItemVisibility(val){
    this.getSubject(Item.SUBJECT_SIDEBAR_VISIBILITY).next(val);
  }

  public setMainSidebarItemVisibility(val){
    this.getSubject(Item.SUBJECT_LEFT_SIDEBAR_MAIN_VISIBILITY).next(val);
  }


  public removeObject(isSilent?, methodsConfig?:{[key:string]:any}){
    if (this.objectRemoved){return}
    let e = new ItemEvent(this, 'removeObject');
    e.isSilent = isSilent || null;
    e.methodName = 'removeObject';
    if (this.config.getInputProp(Item.SET_UP_AREA_INPUT, 'active')){
      alert('Sorry, you can\'t remove activated room layout item');
      return;
    }
    for (let i in methodsConfig){
      if (!methodsConfig.hasOwnProperty(i)){
        continue;
      }
      e[i] = methodsConfig[i];
    }
    this.getObservable('highlight_object').next(false);
    this.emitter.next(e);
    this.objectRemoved = true;
  }

  public getIconUrl(): string {
    return null;
  }

  public getTitle(): string {
    return this.config.getTitle();
  }

  public getCHIId(): string {
    return "Item-" + (this.getId() ? this.getId() : Math.random());
  }

  public getSort(): number {
    return 2;
  }

  public addChild(item: ConcreteHierarchyItemInterface): void {
    this.children.set(item.getCHIId(), item);
  }

  public removeChild(item: ConcreteHierarchyItemInterface): void {
    this.children.delete(item.getCHIId());
  }

  public getChildren(): Observable<ConcreteHierarchyItemInterface[]> {
    return this.childrenObservable$.asObservable();
  }

  public setChildren(observable: Observable<ConcreteHierarchyItemInterface[]>) {
    // this.childrenObservable = observable;
    observable.subscribe(d => this.childrenObservable$.next(d));
  }

  public getParentConcreteId(){
    return this.config.getConcreteLayerId();
  }

  getParentItemId(){
    return null;
  }

  public onSelect(){

    // hide/show details
  }

  setVisibilityComponent(componentName: string, value: boolean) {
    this.visibilityComponents[componentName] = value;
    let isVisible = true;
    for (const cName in this.visibilityComponents) {
      if (this.visibilityComponents.hasOwnProperty(cName)) {
        if (!this.visibilityComponents[cName]) {
          // if ((isCheck && cName === 'StorySlidesComponent')) {
          //   continue;
          // }
          isVisible = false;
          break;
        }
      }
    }
    this.setVisibility(isVisible);
  }

  getMapObject() {
    let result = null;
    this.registeredPlugins.some((plugin) => {
      if (plugin.mapObject) {
        result = plugin.mapObject;
        return true;
      }
    });
    return result;
  }


  /**
   * add inputs to item that were added on server side
   * @param data
   * @private
   */
  private addMissingInputs(data) {
    let inputsFromResultServer = data.input_config || {};
    if (typeof inputsFromResultServer == 'string'){
      try {
        inputsFromResultServer = JSON.parse(inputsFromResultServer);
      }
      catch (e){
        console.error('Can not parse inputs from server item object', e);
      }
    }

    let inputsToAssign = {}
    for (let i in inputsFromResultServer){
      if (!this.config.getInputByName(i)){
        inputsToAssign[i] = inputsFromResultServer[i];
      }
    }

    if (Object.keys(inputsToAssign).length > 0){
      this.config.assignInputs(inputsToAssign);
    }
  }
}


